/**
 * Générateur — Démontrer l'équivalence de deux programmes de calcul.
 * Attendu ID : 160 | Niveau : 4ème | Domaine : Calcul littéral (O24)
 *
 * Source : doc prof « TANOED EXERCISEUR … 4EME », NOMBRE ET CALCUL, cluster 3,
 * O24. Critère de réussite (image page30) :
 *   « Compare les programmes de calcul suivants :
 *     – choisir un nombre, le tripler puis ajouter 15 au résultat ;
 *     – choisir un nombre, lui ajouter 5 puis multiplier le résultat par 3. »
 *   (3n + 15 = 3(n + 5)). Ex.1 : programme donnant toujours le même résultat.
 *
 * Un programme = liste ordonnée d'instructions rendues sous forme canonique
 * « Choisir un nombre ; Multiplier par 3 ; Ajouter 15. », réduite à une forme
 * affine (k, c) en n par `reduire`, et exécutée pas à pas sur des nombres par
 * `executer` (second chemin).
 *
 * Plan de cartes (validé 2026-07-14, historique : le « tableau de valeurs » est
 * devenu la carte « Un contre-exemple suffit » le 2026-09-23, voir plus bas) :
 *   equivalents — comparer deux programmes (qcm conclusion) + vrai/faux
 *                 « ce programme se réduit à … ».
 *   reduire     — écrire l'expression réduite d'un programme (saisie) +
 *                 tableau de valeurs (saisie). Voir les révisions plus bas.
 *   toujours    — programme à résultat constant (Ex.1) : quel résultat ? (qcm).
 *
 * ── Revue 2026-09-23 (vague 153-163) ──────────────────────────────────────
 * Plafond, attendus de fin de 4e (Eduscol, p. 4) : « Il démontre l'équivalence
 * de deux programmes de calcul. » Exemple : « Compare les programmes de calcul
 * suivants : choisir un nombre, le tripler puis ajouter 15 au résultat ;
 * choisir un nombre, lui ajouter 5 puis multiplier le résultat par 3. » Cours :
 * « D'une part … D'autre part … Développées et réduites, les deux expressions
 * sont égales. »
 *   - « Comparer deux programmes de calcul », QCM REFONDU, mode auto, À
 *     ARBITRER : « Ces deux programmes donnent-ils toujours le même résultat ? »
 *     avec des réponses JUSTIFIÉES, expressions en KaTeX, lettre n. Cas
 *     équivalent (« Ajouter p ; Multiplier par q » contre « Multiplier par q ;
 *     Ajouter pq ») : clé « Oui, car q(n + p) = qn + pq pour tout nombre n. » ;
 *     un_seul_terme, exemples_pour_preuve (« Oui, car pour n = 1 les deux
 *     programmes donnent R. », R juste : la conclusion est bonne mais un
 *     exemple ne démontre pas), preuve_exhaustive. Cas non équivalent (la paire
 *     de l'oubli de distribution, « Multiplier par q ; Ajouter p ») : clé
 *     « Non, car q(n + p) = qn + pq, et non qn + p. » ; un_seul_terme,
 *     memes_nombres, preuve_exhaustive ; JAMAIS de contre-exemple chiffré parmi
 *     ces distracteurs, ce serait une seconde bonne réponse. Les options
 *     « seulement pour un nombre de départ nul / positif » sont SUPPRIMÉES :
 *     jamais justes sur 10 000 tirages, aucune erreur d'élève derrière, la
 *     question était un pile ou face Oui / Non ; l'écart de constante tiré au
 *     hasard (± 1, 2, 3) aussi. Contrat exporté (REGLES_DISTRACTEURS_QCM).
 *     Mode auto : A et B sont présentés dans un ordre ou dans l'autre, et
 *     « On note n le nombre choisi au départ. » précède la question parce que
 *     les réponses emploient n. Résidus mesurés, à arbitrer : décider Oui ou
 *     Non suffit dans le cas non équivalent (la clé y est le seul « Non »,
 *     ce qui revient à un contre-exemple, donc à une démonstration juste), et
 *     la clé y est l'option la plus courte (50 % des tirages en tout).
 *   - « Comparer deux programmes de calcul », Vrai/Faux : famille fausse
 *     parentheses_oubliees ajoutée (« ajouter 5 puis multiplier par 3 » donne
 *     n + 15, l'erreur de priorité) ; consigne en phrase, sans la parenthèse
 *     « (départ : le nombre n) » ; ligne « Le programme « … » donne 3n + 15. »
 *     (un programme ne « se réduit » pas, son expression si) ; programmes
 *     distincts dans un tableau ; une à trois vraies sur quatre. Mode auto :
 *     l'ancienne fausse « multiplier par B puis ajouter A donne Bn + AB » est
 *     nommée ordre_inverse, et « ajouter puis multiplier » sort deux fois sur
 *     trois pour que parentheses_oubliees paraisse plus souvent. L'heuristique
 *     « la constante est le nombre ajouté ssi on multiplie d'abord » résolvait
 *     100 % des tableaux, 46,6 % maintenant (tous ceux sans
 *     parentheses_oubliees).
 *   - « L'expression réduite d'un programme » : formes TOUTES distributives
 *     (« Ajouter p ; Multiplier par q », « Ajouter p ; Multiplier par q ;
 *     Ajouter r », « Multiplier par q ; Ajouter p ; Multiplier par r ») : deux
 *     tirages sur trois ne multipliaient jamais une somme ; phrase en KaTeX
 *     « Expression réduite : ⬚n + ⬚ ».
 *   - « Un contre-exemple suffit », carte NOUVELLE (contreexemple_texte_trou_saisie),
 *     mode auto, À ARBITRER : elle remplace le format « tableau de valeurs »
 *     (clé tableau_texte_trou_saisie, SUPPRIMÉE), rangé dans la carte de
 *     l'expression réduite sous le même nom « Saisie trous » que l'autre format,
 *     et qui appliquait un programme à trois nombres sans rien démontrer. Deux
 *     programmes NON équivalents (la paire de l'oubli de distribution, ou deux
 *     programmes de même multiplicateur aux constantes différentes), une valeur
 *     de n de 1 à 9 où les résultats diffèrent (vérifié), deux lignes « Pour
 *     n = 2, le programme A donne ⬚ », puis la conclusion écrite : un
 *     contre-exemple est une démonstration. C'est aussi l'échelon d'entrée que
 *     la note de gen_146 (l. 116-125) destine à 160 : exécuter un programme sur
 *     une valeur.
 *   - « Un programme qui donne toujours le même résultat » : trois structures
 *     au lieu d'une, toutes distributives ; la réponse était le nombre de
 *     l'étape « Ajouter » dans 100 % des tirages, ce nombre est maintenant le
 *     distracteur oubli_distribution ; familles nommées et exportées ; étape
 *     « Soustraire b fois le nombre de départ » (au lieu de « Soustraire le
 *     produit de b par le nombre de départ »). Mode auto, À ARBITRER : les
 *     trois structures (ab ; ab + c ; ac) et deux familles qui sont des
 *     inférences, tout_s_annule (0, ou le dernier nombre ajouté) et
 *     ordre_inverse (abc) ; oubli_distribution et addition_pour_produit sont
 *     celles de la DEPP 2de 2025 q.5.
 *   - Rappel brouillon IMPÉRATIF sur toutes les cartes (RAPPEL_BROUILLON),
 *     après le programme, juste avant la question : il faut écrire
 *     l'expression de chaque programme, développer, réduire.
 *   - La phrase d'origine disait qu'un « solveur » réduisait les programmes
 *     indépendamment : c'était faux, aucun solveur test-side ne couvre 160. Le
 *     second chemin est `executer`, qui fait tourner les programmes pas à pas
 *     sur des nombres, et le test de mesure de la revue relit le texte rendu.
 *
 * ── Révision 2026-09-24 (demande du prof, validée) ────────────────────────
 *   - « L'expression réduite d'un programme » passe en SAISIE LIBRE (clé
 *     reduire_texte, « Saisie ») : la saisie à trous « Expression réduite :
 *     ⬚n + ⬚ » (clé reduire_texte_trou_saisie, SUPPRIMÉE) soufflait la
 *     forme. Toutes les écritures RÉDUITES sont justes, et elles seules
 *     (prof : « réduire, c'est réduit au maximum ») : ordre des termes, « + »
 *     de tête, × entre le nombre et n (4n + 8, 8 + 4n, +8 + 4n, 4 × n + 8) ;
 *     liste finie (comparaison 'expression', ecrituresPolynome), huit
 *     écritures, complète puisque la forme est toujours kn + c. Le champ
 *     écrit n dans la police de KaTeX. Pas de ligne grise : ni × obligatoire
 *     ni carré.
 *
 * INVARIANTS : toute expression affichée ou attendue d'un programme est
 * comparée à son exécution pas à pas sur neuf nombres (throw sinon) ; deux
 * programmes dits équivalents donnent le même résultat sur ces neuf nombres,
 * deux programmes dits non équivalents diffèrent sur la valeur affichée ; un
 * programme dit constant donne la même valeur partout.
 */

import {
    randomChoice, randInt, shuffle, tex, ecrituresPolynome,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 160;

const L_EQUIV = 'Comparer deux programmes de calcul';
const L_REDUIRE = 'L\'expression réduite d\'un programme';
const L_CONTRE = 'Un contre-exemple suffit';
const L_TOUJOURS = 'Un programme qui donne toujours le même résultat';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision du 2026-09-23, par attendu), IMPÉRATIF sur toutes
// les cartes : l'attendu se fait à la main. Après le programme, juste avant la
// question ; il ne souffle aucune méthode.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

const N_DEPART = `<p>On note ${tex('n')} le nombre choisi au départ.</p>`;

// ── Un programme = liste de pas ; réduction à (k, c) en n ────────────────
const renderPas = (pas) => {
    if (pas.t === 'add') return `Ajouter ${pas.v}`;
    if (pas.t === 'mul') return `Multiplier par ${pas.v}`;
    return `Soustraire ${pas.v} fois le nombre de départ`;   // subStartMul
};

const renderProgramme = (pas) => `Choisir un nombre ; ${pas.map(renderPas).join(' ; ')}.`;

const reduire = (pas) => {
    let k = 1; let c = 0;
    for (const p of pas) {
        if (p.t === 'add') c += p.v;
        else if (p.t === 'mul') { k *= p.v; c *= p.v; }
        else k -= p.v;   // subStartMul
    }
    return { k, c };
};

/** Second chemin : le programme exécuté pas à pas sur le nombre n. */
const executer = (pas, n) => pas.reduce((r, p) => {
    if (p.t === 'add') return r + p.v;
    if (p.t === 'mul') return r * p.v;
    return r - p.v * n;
}, n);

const NOMBRES_TEST = [-5, -2, -1, 0, 1, 2, 3, 7, 10];
/** La forme réduite (k, c) est-elle celle du programme exécuté ? throw sinon. */
const verifierReduction = (pas, { k, c }) => {
    if (!NOMBRES_TEST.every((n) => executer(pas, n) === k * n + c)) {
        throw new Error(`gen_160 : ${renderProgramme(pas)} ne donne pas ${k}n + ${c}`);
    }
};
const equivalents = (pasA, pasB) => NOMBRES_TEST.every((n) => executer(pasA, n) === executer(pasB, n));

// ═════════ Carte 1 : Comparer deux programmes ═════════

const PREUVE_EXHAUSTIVE = 'On ne peut pas le savoir sans essayer tous les nombres.';
const MEMES_NOMBRES = 'Oui, car les deux programmes utilisent les mêmes nombres.';

const genEquivQcm = () => {
    for (let essai = 0; essai < 50; essai++) {
        const p = randInt(2, 9);
        const q = randInt(2, 9);
        const equiv = randomChoice([true, false]);
        const ajouterPuis = [{ t: 'add', v: p }, { t: 'mul', v: q }];                    // q(n + p)
        const multiplierPuis = [{ t: 'mul', v: q }, { t: 'add', v: equiv ? p * q : p }]; // qn + pq, ou qn + p
        verifierReduction(ajouterPuis, { k: q, c: p * q });
        verifierReduction(multiplierPuis, { k: q, c: equiv ? p * q : p });
        if (equivalents(ajouterPuis, multiplierPuis) !== equiv) throw new Error('gen_160 : équivalence');
        // A et B dans un ordre ou dans l'autre
        const [progA, progB] = randomChoice([true, false]) ? [ajouterPuis, multiplierPuis] : [multiplierPuis, ajouterPuis];
        const facto = `${q}(n + ${p})`;
        const juste = `${q}n + ${p * q}`;
        const oubli = `${q}n + ${p}`;
        const pourTout = `pour tout nombre ${tex('n')}`;
        let cle; let faux;
        if (equiv) {
            const R = executer(ajouterPuis, 1);
            if (R !== executer(multiplierPuis, 1)) throw new Error('gen_160 : exemple');
            cle = `Oui, car ${tex(`${facto} = ${juste}`)} ${pourTout}.`;
            faux = [
                { code: 'un_seul_terme', texte: `Non, car ${tex(`${facto} = ${oubli}`)} ${pourTout}.` },
                { code: 'exemples_pour_preuve', texte: `Oui, car pour ${tex('n = 1')} les deux programmes donnent ${R}.` },
                { code: 'preuve_exhaustive', texte: PREUVE_EXHAUSTIVE },
            ];
        } else {
            cle = `Non, car ${tex(`${facto} = ${juste}`)}, et non ${tex(oubli)}.`;
            faux = [
                { code: 'un_seul_terme', texte: `Oui, car ${tex(`${facto} = ${oubli}`)} ${pourTout}.` },
                { code: 'memes_nombres', texte: MEMES_NOMBRES },
                { code: 'preuve_exhaustive', texte: PREUVE_EXHAUSTIVE },
            ];
        }
        const choix = [cle, ...faux.map((f) => f.texte)];
        if (new Set(choix).size !== 4) continue;
        const payload = makeQcm(choix, cle,
            `<p><b>Programme A :</b> ${renderProgramme(progA)}</p>`
            + `<p><b>Programme B :</b> ${renderProgramme(progB)}</p>`
            + N_DEPART
            + RAPPEL_BROUILLON
            + '<p><b>Ces deux programmes donnent-ils toujours le même résultat ?</b></p>',
            opts(L_EQUIV));
        payload.contenu = { ...payload.contenu, sousType: equiv ? 'equivalents' : 'non_equivalents' };
        payload._meta.params = { progA, progB, equiv, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_160 : comparer (QCM) sans tirage');
};

/** Une ligne du Vrai/Faux : un programme à deux pas et l'expression qu'on lui prête. */
const tirerLigneEquiv = (vraie, vus) => {
    for (let essai = 0; essai < 60; essai++) {
        const A = randInt(2, 9); const B = randInt(2, 9);
        const forme = randomChoice(['addMul', 'addMul', 'mulAdd']);
        // addMul : « ajouter A puis multiplier par B » → Bn + AB
        // mulAdd : « multiplier par B puis ajouter A » → Bn + A
        const pas = forme === 'addMul' ? [{ t: 'add', v: A }, { t: 'mul', v: B }] : [{ t: 'mul', v: B }, { t: 'add', v: A }];
        const desc = forme === 'addMul' ? `ajouter ${A} puis multiplier par ${B}` : `multiplier par ${B} puis ajouter ${A}`;
        if (vus.includes(desc)) continue;
        const juste = reduire(pas);
        verifierReduction(pas, juste);
        let dite = juste; let code = null;
        if (!vraie) {
            if (forme === 'mulAdd') { code = 'ordre_inverse'; dite = { k: B, c: A * B }; }
            else if (Math.random() < 0.5) { code = 'un_seul_terme'; dite = { k: B, c: A }; }
            else { code = 'parentheses_oubliees'; dite = { k: 1, c: A * B }; }
            if (NOMBRES_TEST.every((n) => executer(pas, n) === dite.k * n + dite.c)) throw new Error('gen_160 : ligne fausse vraie');
        }
        const expr = `${dite.k === 1 ? '' : dite.k}n + ${dite.c}`;
        return { desc, ligne: `Le programme « ${desc} » donne ${tex(expr)}.`, vraie, code };
    }
    return null;
};

const genEquivVf = () => {
    for (let essai = 0; essai < 50; essai++) {
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = [];
        for (const vraie of verites) {
            const l = tirerLigneEquiv(vraie, lignes.map((x) => x.desc));
            if (!l) break;
            lignes.push(l);
        }
        if (lignes.length !== 4 || new Set(lignes.map((l) => l.ligne)).size !== 4) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), lignes.map((l) => (l.vraie ? 0 : 1)),
            `${RAPPEL_BROUILLON}<p><b>Vrai ou faux ?</b> Le nombre de départ est ${tex('n')}.</p>`,
            opts(L_EQUIV));
        payload._meta.params = {
            lignes: lignes.map(({ desc, vraie }) => ({ desc, vraie })),
            distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })),
        };
        return payload;
    }
    throw new Error('gen_160 : Vrai/Faux sans tirage');
};

// ═════════ Carte 2 : L'expression réduite d'un programme ═════════

/** Un programme où l'on multiplie une somme (distributivité), et sa forme réduite. */
const tirerProgrammeDistributif = () => {
    const p = randInt(2, 9); const q = randInt(2, 9); const r = randInt(2, 9);
    const forme = randomChoice(['addMul', 'addMulAdd', 'mulAddMul']);
    if (forme === 'addMul') return { forme, pas: [{ t: 'add', v: p }, { t: 'mul', v: q }] };
    if (forme === 'addMulAdd') return { forme, pas: [{ t: 'add', v: p }, { t: 'mul', v: q }, { t: 'add', v: r }] };
    return { forme, pas: [{ t: 'mul', v: q }, { t: 'add', v: p }, { t: 'mul', v: r }] };
};

// Saisie libre (2026-09-24, décision prof) : toutes les écritures réduites de
// kn + c (ecrituresPolynome : ordre, « + » de tête, k*n), et elles seules.
const genReduireSaisie = () => {
    const { forme, pas } = tirerProgrammeDistributif();
    const { k, c } = reduire(pas);
    verifierReduction(pas, { k, c });
    const ecritures = ecrituresPolynome([{ coef: k, deg: 1 }, { coef: c, deg: 0 }], 'n');
    const payload = makeSaisie(tex(`${k}n + ${c}`),
        `<p><b>Voici un programme de calcul :</b> ${renderProgramme(pas)}</p>`
        + N_DEPART
        + RAPPEL_BROUILLON
        + '<p><b>Écris l\'expression réduite obtenue.</b></p>',
        { ...opts(L_REDUIRE), strict: true, comparaison: 'expression', reponses_acceptees: ecritures });
    payload._meta.params = { forme, pas, k, c };
    return payload;
};

// ═════════ Carte 3 : Un contre-exemple suffit ═════════

const genContreExemple = () => {
    for (let essai = 0; essai < 50; essai++) {
        const p = randInt(2, 9); const q = randInt(2, 9);
        const paire = randomChoice(['oubli_distribution', 'oubli_distribution', 'constantes_differentes']);
        const ajouterPuis = [{ t: 'add', v: p }, { t: 'mul', v: q }];   // qn + pq
        let autre;
        if (paire === 'oubli_distribution') autre = [{ t: 'mul', v: q }, { t: 'add', v: p }];   // qn + p
        else {
            const c = p * q + randomChoice([-3, -2, -1, 1, 2, 3]);             // qn + c, c ≠ pq
            if (c === p) continue;                                               // déjà la paire de l'oubli
            autre = [{ t: 'mul', v: q }, { t: 'add', v: c }];
        }
        const [progA, progB] = randomChoice([true, false]) ? [ajouterPuis, autre] : [autre, ajouterPuis];
        const n = randInt(1, 9);
        const rA = executer(progA, n); const rB = executer(progB, n);
        if (rA === rB) continue;                                                 // la valeur doit les séparer
        verifierReduction(progA, reduire(progA));
        verifierReduction(progB, reduire(progB));
        const ligne = (nom, r) => `$\\text{Pour } n = ${n} \\text{, le programme ${nom} donne } {{${r}}}$`;
        const payload = makeTexteTrou(
            `${ligne('A', rA)}\n${ligne('B', rB)}\nLes résultats sont différents : ces deux programmes ne donnent pas toujours le même résultat.`,
            `<p><b>Programme A :</b> ${renderProgramme(progA)}</p>`
            + `<p><b>Programme B :</b> ${renderProgramme(progB)}</p>`
            + N_DEPART
            + RAPPEL_BROUILLON
            + `<p><b>Calcule le résultat de chaque programme pour ${tex(`n = ${n}`)}.</b></p>`,
            { ...opts(L_CONTRE), mode: 'saisie' });
        payload._meta.params = { paire, progA, progB, n, rA, rB };
        return payload;
    }
    throw new Error('gen_160 : contre-exemple sans tirage');
};

// ═════════ Carte 4 : Programme à résultat constant (Ex.1) ═════════

/**
 * Trois structures, toutes distributives, chacune avec trois distracteurs
 * nommés, distincts de la clé et entre eux PAR CONSTRUCTION (conditions
 * rappelées à chaque structure, vérifiées ensuite).
 */
const STRUCTURES_TOUJOURS = {
    // Ajouter a ; Multiplier par b ; Soustraire b fois n → b(n + a) − bn = ab
    ajouter_multiplier: () => {
        const a = randInt(2, 9); const b = randInt(2, 6);
        if (a === 2 && b === 2) return null;                 // ab = a + b
        return {
            pas: [{ t: 'add', v: a }, { t: 'mul', v: b }, { t: 'subStartMul', v: b }],
            faux: [
                { code: 'oubli_distribution', v: a },
                { code: 'addition_pour_produit', v: a + b },
                { code: 'tout_s_annule', v: 0 },
            ],
        };
    },
    // Ajouter a ; Multiplier par b ; Soustraire b fois n ; Ajouter c → ab + c
    ajouter_multiplier_ajouter: () => {
        const a = randInt(2, 9); const b = randInt(2, 6); const c = randInt(2, 9);
        if (a === 2 && b === 2) return null;                 // ab + c = a + b + c
        return {
            pas: [{ t: 'add', v: a }, { t: 'mul', v: b }, { t: 'subStartMul', v: b }, { t: 'add', v: c }],
            faux: [
                { code: 'oubli_distribution', v: a + c },
                { code: 'addition_pour_produit', v: a + b + c },
                { code: 'tout_s_annule', v: c },
            ],
        };
    },
    // Multiplier par b ; Ajouter a ; Multiplier par c ; Soustraire bc fois n → c(bn + a) − bcn = ac
    multiplier_ajouter_multiplier: () => {
        const a = randInt(2, 9); const b = randInt(2, 5); const c = randInt(2, 5);
        if (a === b) return null;                            // ac serait écrit dans l'étape « Soustraire bc fois »
        if (a === 2 && c === 2) return null;                 // ac = a + c
        return {
            pas: [{ t: 'mul', v: b }, { t: 'add', v: a }, { t: 'mul', v: c }, { t: 'subStartMul', v: b * c }],
            faux: [
                { code: 'oubli_distribution', v: a },
                { code: 'addition_pour_produit', v: a + c },
                { code: 'ordre_inverse', v: a * b * c },
            ],
        };
    },
};

const genToujoursQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const structure = randomChoice(Object.keys(STRUCTURES_TOUJOURS));
        const t = STRUCTURES_TOUJOURS[structure]();
        if (!t) continue;
        const { k, c } = reduire(t.pas);
        verifierReduction(t.pas, { k, c });
        if (k !== 0) throw new Error(`gen_160 : programme non constant (${structure})`);
        const choix = [String(c), ...t.faux.map((f) => String(f.v))];
        if (new Set(choix).size !== 4) throw new Error(`gen_160 : choix confondus (${structure}) ${choix.join(' ; ')}`);
        const payload = makeQcm(choix, String(c),
            `<p><b>Voici un programme de calcul :</b> ${renderProgramme(t.pas)}</p>`
            + RAPPEL_BROUILLON
            + '<p><b>Quel résultat obtient-on, quel que soit le nombre choisi au départ ?</b></p>',
            opts(L_TOUJOURS));
        payload._meta.params = { structure, pas: t.pas, constante: c, distracteurs: t.faux.map(({ code }) => ({ code })) };
        return payload;
    }
    throw new Error('gen_160 : programme constant sans tirage');
};

// ═════════ Contrat distracteurs (référence de revue, jamais lu à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'programmes écrits « Choisir un nombre ; … » ; expressions en KaTeX, lettre n ; chaque expression comparée à l\'exécution pas à pas du programme sur neuf nombres (throw)',
        'trois distracteurs garantis par construction, re-tirage borné sinon, jamais d\'option inventée à l\'assemblage',
    ],
    equivalents_qcm_unique: {
        support: 'A « Ajouter p ; Multiplier par q » (q(n + p)) et B « Multiplier par q ; Ajouter pq » (équivalents) ou « Multiplier par q ; Ajouter p » (non équivalents, la paire de l\'oubli de distribution), p et q de 2 à 9, A et B dans un ordre ou l\'autre ; réponses justifiées (mode auto, À ARBITRER)',
        familles: {
            un_seul_terme: 'q(n + p) = qn + p : le multiplicateur ne porte que sur n ; DEPP 2de 2025 q.5 (20,3 %), cadrage 158-163 ; conduit à « Non » quand les programmes sont équivalents, à « Oui » quand ils ne le sont pas',
            exemples_pour_preuve: 'cas équivalent seulement : « Oui, car pour n = 1 les deux programmes donnent R » (R juste) : la conclusion est bonne, mais un exemple ne démontre pas ; cadrage 158-163, repères de progression 2019',
            preuve_exhaustive: '« On ne peut pas le savoir sans essayer tous les nombres » : la conception empirique de la preuve, qui ne voit ni le calcul littéral ni le contre-exemple',
            memes_nombres: 'cas non équivalent seulement : « les deux programmes utilisent les mêmes nombres » : juger sur les nombres écrits et non sur l\'ordre des opérations (inférence)',
        },
        supprimees: {
            seulement_nul: '« Ils donnent le même résultat seulement pour un nombre de départ nul » : jamais juste (0 sur 10 000), aucune erreur d\'élève derrière',
            seulement_positif: '« … seulement pour un nombre de départ positif » : idem',
            ecart_au_hasard: 'programme non équivalent « Ajouter pq ± 1, 2 ou 3 » : un écart tiré au hasard, sans erreur derrière ; remplacé par l\'oubli de distribution',
            contre_exemple_chiffre: 'JAMAIS dans le cas non équivalent : « Non, car pour n = 1, A donne … et B … » serait une seconde bonne réponse',
        },
        garantie: 'quatre textes distincts par construction (conclusions, égalités ou justifications différentes) ; la clé est la seule réponse juste ET justifiée',
    },
    toujours_qcm_unique: {
        support: 'trois structures distributives et constantes : « Ajouter a ; Multiplier par b ; Soustraire b fois le nombre de départ » (ab), la même suivie de « Ajouter c » (ab + c), « Multiplier par b ; Ajouter a ; Multiplier par c ; Soustraire bc fois le nombre de départ » (ac)',
        familles: {
            oubli_distribution: 'le multiplicateur ne porte pas sur le nombre ajouté (b(n + a) = bn + a) : la réponse devient le nombre ajouté, a (a + c, a) ; DEPP 2de 2025 q.5, un_seul_terme',
            addition_pour_produit: 'b(n + a) = bn + (b + a) : additionner au lieu de multiplier ; DEPP 2de 2025 q.5 (15x + 4)',
            tout_s_annule: 'l\'élève voit « Multiplier par b » et « Soustraire b fois le nombre de départ » s\'annuler et croit que tout ce qui précède disparaît : 0, ou le dernier nombre ajouté c (inférence)',
            ordre_inverse: 'structure « Multiplier par b ; Ajouter a ; … » calculée comme b(n + a) : on ajoute avant de multiplier, abc (inférence)',
        },
        supprimees: {
            anciens_distracteurs: 'a × b, a + b, b, a + 1 d\'une structure unique « Multiplier par b ; Ajouter a ; Soustraire … » dont la réponse était toujours le nombre de l\'étape « Ajouter » : aucun nommé, et la réponse se lisait dans le programme',
        },
        garantie: 'ab ≠ a (b ≥ 2), ab ≠ a + b hors a = b = 2 écarté, ab + c ≠ c, ac ≠ a (c ≥ 2), ac ≠ a + c hors a = c = 2 écarté, ac ≠ abc (b ≥ 2), a + c ≠ abc ; a ≠ b dans la troisième structure, sinon la réponse ac serait écrite dans l\'étape « Soustraire bc fois » ; vérifié (throw)',
    },
    equivalents_tableau_vf: 'quatre programmes distincts à deux pas, une à trois lignes vraies ; fausses : un_seul_terme (ajouter A puis multiplier par B → Bn + A), parentheses_oubliees (→ n + AB, l\'erreur de priorité), ordre_inverse (multiplier par B puis ajouter A → Bn + AB)',
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    equivalents_qcm_unique: genEquivQcm,
    equivalents_tableau_vf: genEquivVf,
    reduire_texte: genReduireSaisie,
    contreexemple_texte_trou_saisie: genContreExemple,
    toujours_qcm_unique: genToujoursQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', texte: 'Saisie', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'equivalents', label: L_EQUIV, formatsList: buildFormats('equivalents') },
    { id: 'reduire', label: L_REDUIRE, formatsList: buildFormats('reduire') },
    { id: 'contreexemple', label: L_CONTRE, formatsList: buildFormats('contreexemple') },
    { id: 'toujours', label: L_TOUJOURS, formatsList: buildFormats('toujours') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
